'use client'

import { RadioGroup } from '@headlessui/react'
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import clsx from 'clsx'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum, ConsultationMode } from '../../model/bookingTypes'
import { BookingStep } from './BookingStep'
import { useGetMeetingLink } from '@/shared/api/meeting-link'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'

export function ConsultationModeStep() {
  const { state, dispatch, goToStep, isStepCompleted } = useBooking()
  const { meetingLink } = useGetMeetingLink(state.meetingLinkSlug)

  const isImmediateEnabled = (meetingLink?.immediateConsultationConfig?.enabled && !!meetingLink?.waitingRoom) ?? false
  const isCompleted = isStepCompleted(BookingStepEnum.CONSULTATION_MODE)

  const handleModeSelect = (mode: ConsultationMode) => {
    dispatch({
      type: 'SET_CONSULTATION_MODE',
      payload: mode,
      goToNextStep: true,
    })
  }

  if (state.currentStep === BookingStepEnum.CONSULTATION_MODE) {
    return (
      <BookingStep>
        <BookingStep.Title>
          <Trans>Consultation mode</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <RadioGroup value={state.consultationMode} onChange={handleModeSelect}>
            <RadioGroup.Label className="sr-only">
              <Trans>Consultation mode</Trans>
            </RadioGroup.Label>
            <div className="space-y-4">
              <RadioGroup.Option value={ConsultationMode.Scheduled} data-testid="consultation-mode-scheduled">
                {({ checked }) => (
                  <div
                    className={clsx(
                      'p-5 border-2 rounded-xl cursor-pointer transition-all flex items-center space-x-4',
                      checked ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-gray-200 hover:border-gray-300 bg-white'
                    )}
                  >
                    <div className={clsx('p-3 rounded-lg', checked ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500')}>
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">
                        <Trans>Schedule an appointment</Trans>
                      </p>
                      <p className="text-sm text-gray-500">
                        <Trans>Choose an available slot in advance</Trans>
                      </p>
                    </div>
                  </div>
                )}
              </RadioGroup.Option>

              <RadioGroup.Option value={ConsultationMode.Immediate} data-testid="consultation-mode-immediate" disabled={!isImmediateEnabled}>
                {({ checked, disabled }) => (
                  <div
                    className={clsx(
                      'p-5 border-2 rounded-xl transition-all flex items-center space-x-4',
                      disabled ? 'opacity-50 cursor-not-allowed border-gray-100 bg-gray-50' : 'cursor-pointer',
                      !disabled && checked
                        ? 'border-green-600 bg-green-50 ring-1 ring-green-600/20'
                        : !disabled && 'border-gray-200 hover:border-gray-300 bg-white'
                    )}
                  >
                    <div className={clsx('p-3 rounded-lg', !disabled && checked ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500')}>
                      <ClockIcon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">
                        <Trans>Consult now</Trans>
                      </p>
                      {isImmediateEnabled && (
                        <p className="text-sm text-green-600">
                          <Trans>Available</Trans>
                        </p>
                      )}
                      {!isImmediateEnabled && (
                        <p className="text-sm text-gray-400">
                          <Trans>Not available currently</Trans>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </RadioGroup.Option>
            </div>
          </RadioGroup>
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return (
      <Summary
        icon="⏱️"
        label={t`Consultation mode`}
        onEdit={() => goToStep(BookingStepEnum.CONSULTATION_MODE)}
        text={state.consultationMode === ConsultationMode.Immediate ? t`Consult now` : t`Schedule an appointment`}
      />
    )
  }

  return (
    <IncompletedStepLabel icon="⏱️">
      <Trans>Consultation mode</Trans>
    </IncompletedStepLabel>
  )
}
